import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles, FileText } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR") : "";

// Choix du document source (PDF le plus probable pré-sélectionné) puis analyse IA.
export default function AnalyzeSourceDialog({ contractId, open, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { return; }
    setData(null);
    api.get(`/contracts/${contractId}/ai-sources`).then((r) => {
      setData(r.data);
      const cur = r.data.candidates.find((c) => c.file_asset_id === r.data.current_file_asset_id);
      setPicked(cur?.id || r.data.suggested_id);
    }).catch((e) => { toast.error(apiError(e)); onClose(); });
  }, [open, contractId]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/contracts/${contractId}/analyze`, { document_id: picked });
      toast.success(`Analyse terminée · ${r.filled.length} champ(s) complété(s)`
        + (r.discrepancies.length ? ` · ${r.discrepancies.length} écart(s) à vérifier` : "")
        + ` · score risque ${r.risk_score}/100`);
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-xl" data-testid="ai-source-dialog">
        <DialogHeader>
          <DialogTitle>Analyser avec l'IA</DialogTitle>
          <DialogDescription>
            Choisis le document du contrat à analyser. Les champs vides seront complétés,
            les écarts avec ta saisie te seront signalés.
          </DialogDescription>
        </DialogHeader>
        {!data ? (
          <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : data.candidates.length === 0 ? (
          <p className="text-sm text-slate-500 py-4">Aucun PDF ou image dans les dossiers de ce contrat.
            Ajoute le contrat signé dans un dossier puis relance.</p>
        ) : (
          <div className="max-h-72 overflow-y-auto space-y-1">
            {data.candidates.map((c) => (
              <button key={c.id} type="button" onClick={() => setPicked(c.id)}
                      className={cn("w-full flex items-center gap-2 rounded border p-2 text-left text-xs transition-colors",
                        picked === c.id ? "border-blue-500 bg-blue-50" : "hover:bg-slate-50")}
                      data-testid={`ai-source-${c.id}`}>
                <FileText className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                <span className="flex-1 truncate font-medium">{c.name}</span>
                <span className="text-slate-400 truncate max-w-[140px]">{c.folder}</span>
                <span className="text-slate-400">{fmtDate(c.uploaded_at)}</span>
                {c.id === data.suggested_id && <span className="text-[10px] text-blue-700">suggéré</span>}
              </button>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy} data-testid="ai-source-cancel">Annuler</Button>
          <Button onClick={run} disabled={!picked || busy} className="bg-blue-600 hover:bg-blue-700"
                  data-testid="ai-source-run">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
            {busy ? "Analyse en cours (≈ 20 s)…" : "Analyser"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
